/** Scene routing with a quick warm fade (skipped when animations are off). */
import type * as Phaser from 'phaser';
import { dur } from '../fx/motion.js';
import type { AppContext } from '../game/context.js';
import type { CustomRound } from '../services/customRound.js';

export type SceneKey = 'boot' | 'map' | 'shop' | 'play';

export function go(from: Phaser.Scene, key: SceneKey, data?: object): void {
  const d = dur(160);
  if (!d) {
    from.scene.start(key, data);
    return;
  }
  const cam = from.cameras.main;
  let done = false;
  const start = () => {
    if (done) return;
    done = true;
    from.scene.start(key, data);
  };
  cam.fadeOut(d, 255, 226, 188);
  cam.once('camerafadeoutcomplete', start);
  // the fade runs on the frame clock; the route change must not wait on dropped frames
  setTimeout(start, d + 250);
}

/** The scene on screen (map, shop or play), for features that change scenes. */
export function activeScene(a: AppContext): Phaser.Scene | null {
  return a.game?.scene.getScenes(true).find((x) => x.scene.key !== 'boot') ?? null;
}

/**
 * Play a feature-launched round (daily, tournament, event, beat, duel): see
 * services/customRound.ts for the contract. False when no scene can take it yet.
 */
export function startCustomRound(a: AppContext, custom: CustomRound): boolean {
  const sc = activeScene(a);
  if (!sc) return false;
  go(sc, 'play', { level: custom.level.number, custom });
  return true;
}

export function fadeIn(scene: Phaser.Scene): void {
  const d = dur(200);
  if (d) scene.cameras.main.fadeIn(d, 255, 226, 188);
}

/** Restart a scene (e.g. after a resize) once the screen size settles. */
export function onResizeSettled(scene: Phaser.Scene, cb: () => void): void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let last = `${scene.scale.width}x${scene.scale.height}`;
  const handler = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      const now = `${scene.scale.width}x${scene.scale.height}`;
      if (now !== last && scene.scene.isActive()) {
        last = now;
        cb();
      }
    }, 180);
  };
  scene.scale.on('resize', handler);
  scene.events.once('shutdown', () => {
    scene.scale.off('resize', handler);
    if (timer) clearTimeout(timer);
  });
}
